import React from 'react';
import VisaPageLayout, { VisaPlan, RequirementItem, ProcessStep, FaqItem } from './visa-page-layout';
import { useVisaPacksByCountry } from '../hooks/useVisaPacksByCountry';

// NOTE ON CONTENT: fees, processing times and document rules below are
// realistic placeholders matching India's tourist e-visa structure.
// Confirm current figures with the official Indian e-Visa portal
// (indianvisaonline.gov.in) before publishing, and keep this file as the
// single place to update them.

const plans: VisaPlan[] = [
  { id: 'in-30', name: '30-Day e-Tourist Visa', entryType: 'Double Entry', validity: 'Use within 30 days of issue', stay: 'Up to 30 days in India', processing: '3–5 business days', price: '30', currency: 'OMR' },
  { id: 'in-1y', name: '1-Year e-Tourist Visa', entryType: 'Multiple Entry', validity: 'Valid for 1 year from issue', stay: 'Up to 90 days per visit', processing: '4–6 business days', price: '55', currency: 'OMR', featured: true },
  { id: 'in-5y', name: '5-Year e-Tourist Visa', entryType: 'Multiple Entry', validity: 'Valid for 5 years from issue', stay: 'Up to 90 days per visit', processing: '5–7 business days', price: '95', currency: 'OMR' },
];

const requirements: RequirementItem[] = [
  { label: 'Passport copy', detail: 'Valid for at least 6 months from your arrival date, with at least two blank pages.' },
  { label: 'Passport-size photo', detail: 'Recent, plain white background, front-facing, matching Indian e-Visa photo specifications.' },
  { label: 'Confirmed return ticket', detail: 'Round-trip or onward flight booking showing your departure from India.' },
  { label: 'Accommodation details', detail: 'Hotel bookings or itinerary covering your planned stay.' },
  { label: 'Recent bank statement', detail: 'Proof of sufficient funds for the length of your trip.' },
];

const goodToKnow: string[] = [
  'The e-Visa is processed as an Electronic Travel Authorization — you\'ll still get a passport stamp on arrival, so keep a printed copy of your approval to show at immigration.',
  'India\'s e-Visa is only valid for entry through designated airports and seaports — we\'ll confirm your entry point is eligible.',
  'The 1-year and 5-year options suit travelers planning repeat visits, but each individual stay is still capped at 90 days.',
  'Certain border areas require additional permits — tell us your full itinerary and we\'ll flag anything that needs extra paperwork.',
];

const steps: ProcessStep[] = [
  { title: 'Upload your documents', text: 'Submit your passport scan, photo and travel details through our secure form.' },
  { title: 'We review & submit', text: 'Our team checks your file against current Indian e-Visa requirements before filing.' },
  { title: 'Track your status', text: 'We\'ll update you by WhatsApp or email while your application is processed.' },
  { title: 'Receive your e-Visa', text: 'Download your approved e-Visa and print a copy to present at Indian immigration.' },
];

const faqs: FaqItem[] = [
  { question: 'How far in advance should I apply for an Indian e-Visa?', answer: 'We recommend at least 7–10 days before departure, since e-Visa applications can only be submitted within a set window before travel and occasionally require follow-up documents.' },
  { question: 'Which entry points accept the e-Visa?', answer: 'The e-Visa is accepted at a specific list of international airports and seaports. Tell us your arrival point and we\'ll confirm it\'s eligible before you apply.' },
  { question: 'Can I extend my e-Tourist Visa once I\'m in India?', answer: 'Tourist e-Visas generally cannot be extended or converted. If you need a longer stay, we can advise on applying for a different visa category instead.' },
  { question: 'Do I need a separate visa for each state I visit?', answer: 'No, a single e-Visa covers travel across India, though a few protected or restricted areas require additional permits — we\'ll flag these based on your itinerary.' },
];

export default function VisaIndiaPage() {

    const { plans, loading, error } = useVisaPacksByCountry('IND');
    
  return (
    <VisaPageLayout
      countryName="India"
      flagEmoji="🇮🇳"
      heroEyebrow="Republic of India"
      heroImage="https://images.unsplash.com/photo-1759058441220-c5f17193fee6?w=1600&q=80&fit=crop&crop=center"
      heroTitleLead="Your"
      heroTitleHighlight="India"
      heroTitleTail="e-Visa, prepared with care"
      heroSubtitle="From the Taj Mahal at sunrise to the backwaters of Kerala — we prepare your Indian e-Visa application so the details are settled long before you land in Delhi or Mumbai."
      trustStats={[
        { value: '3–7', label: 'Business Days' },
        { value: '95%', label: 'Approval Rate' },
        { value: '3,500+', label: 'India Visas Issued' },
      ]}
      plans={plans}
      plansLoading={loading}
      plansError={error}
      requirements={requirements}
      goodToKnow={goodToKnow}
      steps={steps}
      faqs={faqs}
      ctaSubtitle="Send your documents today — most complete e-Visa files are submitted to Indian immigration within one business day."
    />
  );
}